import { Node, type NodeOptions } from "./Node";
import { IBoundingBox } from "../../interfaces/IBoundingBox";
import { ITraversable } from "../../interfaces/ITraversable";

import type {
    IBackgroundStyle,
    IBorderStyle,
    IColorStyle,
    IFontStyle,
} from "../Styles";

export type GroupOptions = NodeOptions;

/**
 * 抽象组类，表示可以包含其他节点的容器
 */
export abstract class Group extends Node implements ITraversable {

    /** 容器内包含的节点 */
    protected _children: Node[] = [];
    // 是否允许溢出/是否隐藏溢出内容
    private _overflowable: boolean = false;

    /**
     * Node 构造函数
     * @param options 可选的构造参数对象
     */
    constructor(options?: GroupOptions) {
        const { id, zoomFactorBase } = options || {};
        super({ id, zoomFactorBase });
    }

    /** 获取 是否允许溢出/是否隐藏溢出内容 */
    get overflowable(): boolean { return this._overflowable; }
    /** 设置 是否允许溢出/是否隐藏溢出内容 */
    protected set overflowable(value: boolean) { this._overflowable = value; }

    /** 设置父节点，同时更新所有子节点的层级 */
    override set parent(value: Node | undefined) {
        super.parent = value; // 调用基类的 setter，更新自身层级
        this.updateChildrenLevels(); // 更新所有子节点层级
    }
    /** 获取父节点 */
    override get parent(): Node | undefined {
        return super.parent;
    }

    /**
     * 获取当前组的子节点列表
     * @returns 子节点数组
     */
    get children(): Node[] {
        return this._children;
    }

    /**
     * 检查当前组是否包含指定的子节点
     * @param node 要检查的节点
     * @returns 如果节点是当前组的直接子节点，则返回 true，否则返回 false
     */
    contains(node: Node): boolean {
        return this._children.includes(node);
    }

    /**
     * 添加子节点
     * @param child 要添加的节点
     * @returns 当前实例，用于链式调用
     */
    addChild(child: Node): this {
        this._children.push(child);
        child.parent = this; // 设置父节点引用，这会触发 updateLevel
        child.updateGlobalTransform(); // 更新子节点的全局变换
        return this;
    }

    /**
     * 移除子节点
     * @param child 要移除的节点
     * @returns 当前实例，用于链式调用
     */
    removeChild(child: Node): this {
        const index = this._children.indexOf(child);
        if (index >= 0) {
            this._children.splice(index, 1);
            child.parent = undefined; // 移除父节点引用
        }
        return this;
    }

    /**
     * 计算组内所有子节点的包围盒
     * @returns 包含所有子节点的最小包围盒，如果没有子节点则返回一个原点处的零尺寸包围盒
     */
    getBoundingBox(): IBoundingBox {
        if (this._children.length === 0) {
            // 对于空 Group，返回一个在原点，宽高为0的包围盒
            // 或者可以根据 Group 自身是否有尺寸来决定，但目前 Group 没有直接的宽高属性
            const ownGlobalPos = this.globalPosition;
            return {
                width: 0, height: 0,
                x: ownGlobalPos.x, y: ownGlobalPos.y,
                left: ownGlobalPos.x, top: ownGlobalPos.y,
                right: ownGlobalPos.x, bottom: ownGlobalPos.y
            };
        }

        let minX = Infinity;
        let minY = Infinity;
        let maxX = -Infinity;
        let maxY = -Infinity;

        // 遍历所有子节点，计算全局坐标系下的边界
        for (const child of this._children) {
            // 定义四个角点（子节点的局部坐标系）
            const localCorners = [
                { x: 0, y: 0 },                         // 左上角
                { x: child.width, y: 0 },               // 右上角
                { x: 0, y: child.height },              // 左下角
                { x: child.width, y: child.height }     // 右下角
            ];

            // 将角点转换为全局坐标系
            for (const localCorner of localCorners) {
                // 应用子节点的全局变换矩阵
                const globalPoint = child.globalMatrix.transformPoint(localCorner.x, localCorner.y);

                // 更新包围盒边界
                minX = Math.min(minX, globalPoint.x);
                minY = Math.min(minY, globalPoint.y);
                maxX = Math.max(maxX, globalPoint.x);
                maxY = Math.max(maxY, globalPoint.y);
            }

            // 如果子节点是 Group，递归获取其包围盒并合并
            // 注意：如果一个 Group 本身没有子节点，其 getBoundingBox 会返回其自身位置的零尺寸包围盒
            // 这意味着不需要特别处理 childBoundingBox 为 null 的情况，因为它总会返回一个 IBoundingBox
            if (child instanceof Group) {
                const childBoundingBox = child.getBoundingBox();
                // 只有当子组的包围盒有效（即宽度或高度不为0，或者它不是初始的Infinity值）时才合并
                // 或者更简单地，总是合并，因为Infinity会被任何实际数值覆盖
                minX = Math.min(minX, childBoundingBox.left);
                minY = Math.min(minY, childBoundingBox.top);
                maxX = Math.max(maxX, childBoundingBox.right);
                maxY = Math.max(maxY, childBoundingBox.bottom);
            }
        }

        // 如果所有子节点都是零尺寸且位于同一位置，或者没有有效子节点进行计算（例如，初始值未被覆盖）
        // 此时 minX, minY 可能仍然是 Infinity, maxX, maxY 是 -Infinity
        // 这种情况下，返回一个基于组自身位置的零尺寸包围盒
        if (minX === Infinity || minY === Infinity || maxX === -Infinity || maxY === -Infinity) {
            const ownGlobalPos = this.globalPosition;
            return {
                width: 0, height: 0,
                x: ownGlobalPos.x, y: ownGlobalPos.y,
                left: ownGlobalPos.x, top: ownGlobalPos.y,
                right: ownGlobalPos.x, bottom: ownGlobalPos.y
            };
        }

        // 创建并返回包围盒
        const boundingBox: IBoundingBox = {
            x: minX,
            y: minY,
            width: maxX - minX,
            height: maxY - minY,
            left: minX,
            top: minY,
            right: maxX,
            bottom: maxY
        };

        return boundingBox;
    }

    /**
     * 计算组内所有子节点在局部坐标系中的包围盒
     * @returns 包含所有子节点的最小包围盒，如果没有子节点则返回一个原点处的零尺寸包围盒
     */
    getLocalBoundingBox(): IBoundingBox {
        if (this._children.length === 0) {
            // 对于空 Group，返回一个在原点，宽高为0的包围盒
            return {
                width: 0, height: 0,
                x: 0, y: 0,
                left: 0, top: 0,
                right: 0, bottom: 0
            };
        }

        let minX = Infinity;
        let minY = Infinity;
        let maxX = -Infinity;
        let maxY = -Infinity;

        // 遍历所有子节点，计算局部坐标系下的边界
        for (const child of this._children) {
            // 定义四个角点（子节点的局部坐标系）
            const localCorners = [
                { x: 0, y: 0 },                     // 左上角
                { x: child.width, y: 0 },           // 右上角
                { x: 0, y: child.height },          // 左下角
                { x: child.width, y: child.height } // 右下角
            ];

            // 将角点转换为当前 Group 的局部坐标系
            for (const corner of localCorners) {
                // 应用子节点的局部变换矩阵
                const transformedPoint = child.localMatrix.transformPoint(corner.x, corner.y);

                // 更新包围盒边界
                minX = Math.min(minX, transformedPoint.x);
                minY = Math.min(minY, transformedPoint.y);
                maxX = Math.max(maxX, transformedPoint.x);
                maxY = Math.max(maxY, transformedPoint.y);
            }

            // 如果子节点是 Group，递归获取其局部包围盒并合并
            if (child instanceof Group) {
                const childLocalBoundingBox = child.getLocalBoundingBox();

                // 将子 Group 的局部包围盒转换到当前 Group 的局部坐标系
                const corners = [
                    { x: childLocalBoundingBox.left, y: childLocalBoundingBox.top },       // 左上角
                    { x: childLocalBoundingBox.right, y: childLocalBoundingBox.top },      // 右上角
                    { x: childLocalBoundingBox.left, y: childLocalBoundingBox.bottom },    // 左下角
                    { x: childLocalBoundingBox.right, y: childLocalBoundingBox.bottom }    // 右下角
                ];

                for (const corner of corners) {
                    // 应用子节点的局部变换矩阵
                    const transformedPoint = child.localMatrix.transformPoint(corner.x, corner.y);

                    // 更新包围盒边界
                    minX = Math.min(minX, transformedPoint.x);
                    minY = Math.min(minY, transformedPoint.y);
                    maxX = Math.max(maxX, transformedPoint.x);
                    maxY = Math.max(maxY, transformedPoint.y);
                }
            }
        }

        // 处理边界情况：如果没有有效子节点或所有子节点都是零尺寸
        if (minX === Infinity || minY === Infinity || maxX === -Infinity || maxY === -Infinity) {
            return {
                width: 0, height: 0,
                x: 0, y: 0,
                left: 0, top: 0,
                right: 0, bottom: 0
            };
        }

        // 创建并返回包围盒
        const boundingBox: IBoundingBox = {
            x: minX,
            y: minY,
            width: maxX - minX,
            height: maxY - minY,
            left: minX,
            top: minY,
            right: maxX,
            bottom: maxY
        };

        return boundingBox;
    }

    /** 清空所有子节点 */
    abstract clear(): void;

    /** 更新自身及所有子元素的全局变换 */
    override updateGlobalTransform(): void {
        super.updateGlobalTransform(); // 更新自身全局变换

        // 更新所有子元素的全局变换
        for (const child of this._children) {
            child.updateGlobalTransform();
        }
    }

    /**
     * 应用字体样式
     * @param fontStyle - 要应用的字体样式对象
     */
    override applyFont(fontStyle: IFontStyle): void {
        super.applyFont(fontStyle);
        this.target.applyFont(fontStyle);
    }

    /**
     * 应用颜色样式
     * @param colorStyle - 要应用的颜色样式对象
     */
    override applyColor(colorStyle: IColorStyle): void {
        super.applyColor(colorStyle);
        this.target.applyColor(colorStyle);
    }

    /**
     * 应用边框样式
     * @param borderStyle - 要应用的边框样式对象
     */
    override applyBorder(borderStyle: IBorderStyle): void {
        super.applyBorder(borderStyle);
        this.target.applyBorder(borderStyle);
    }

    /**
     * 应用背景样式
     * @param backgroundStyle - 要应用的背景样式对象
     */
    override applyBackground(backgroundColor: IBackgroundStyle): void {
        super.applyBackground(backgroundColor);
        this.target.applyBackground(backgroundColor);
    }

    // 更新所有子节点层级的方法（递归）
    private updateChildrenLevels(): void {
        for (const child of this._children) {
            child.updateLevel();
            // 如果子节点是 Group，递归更新其子节点
            if (child instanceof Group) {
                child.updateChildrenLevels();
            }
        }
    }
}